import SuperCampaignsService from "@/src/api/SuperCampaignsService.js";
import IntegrationService from "@/src/api/IntegrationService.js";
import { toGetAllEntityLists } from "@/src/utils/utils.js";
import {
  ENTITY_TYPES,
  DEFAULT_CAMPAIGN_FORM_STATE,
} from "@/src/constants/campaignInEngageConstants.js";
import {
  DEFAULT_WHATSAPP_CAMPAIGN_DATA,
  TEMPLATE_FORMATS,
} from "@/src/constants/whatsappCampaignConstants.js";

const state = {
  campaignId: null,
  campaignMessageId: null,
  campaignData: { ...DEFAULT_WHATSAPP_CAMPAIGN_DATA },
  formState: { ...DEFAULT_CAMPAIGN_FORM_STATE },
  recipientListOptions: [],
  excludeOptions: [],
  whatsappConfigs: [],
  recipientCount: 0,
  isLoadingRecipientCount: false,
  isInitializing: true,
  isSaving: false,
  originalCampaignData: null,
};

const getters = {
  hasUnsavedChanges: (state) => {
    if (!state.originalCampaignData) {
      return !!(
        state.campaignData.name ||
        state.campaignData.recipientLists?.length > 0 ||
        state.campaignData.messageTemplate ||
        state.campaignData.messageContent ||
        state.campaignData.ctaButtons?.length > 0
      );
    }
    return JSON.stringify(state.campaignData) !== JSON.stringify(state.originalCampaignData);
  },

  selectedRecipientListNames: (state) => {
    if (!state.campaignData.recipientLists?.length) return [];
    return state.campaignData.recipientLists.map((id) => {
      const list = state.recipientListOptions.find((l) => l.id === id);
      return list ? list.name : `List ${id}`;
    });
  },

  selectedExcludedListNames: (state) => {
    if (!state.campaignData.excludedLists?.length) return [];
    return state.campaignData.excludedLists.map((id) => {
      const list = state.excludeOptions.find((l) => l.id === id);
      return list ? list.name : `List ${id}`;
    });
  },

  selectedWhatsappConfigName: (state) => {
    if (!state.campaignData.whatsappConfigId) return "";
    const config = state.whatsappConfigs.find(
      (c) => c.id === state.campaignData.whatsappConfigId
    );
    return config ? config.name || config.phone_number || `Config ${config.id}` : "";
  },

  filteredRecipientOptions: (state) => {
    if (!state.recipientListOptions?.length) return [];
    return state.recipientListOptions.filter((list) => {
      if (state.campaignData.excludeSome && state.campaignData.excludedLists.includes(list.id)) {
        return false;
      }
      return true;
    });
  },

  filteredExcludeOptions: (state) => {
    if (!state.excludeOptions?.length) return [];
    return state.excludeOptions.filter(
      (list) => !state.campaignData.recipientLists.includes(list.id)
    );
  },
};

const mutations = {
  SET_CAMPAIGN_ID(state, id) {
    state.campaignId = id;
  },

  SET_CAMPAIGN_MESSAGE_ID(state, id) {
    state.campaignMessageId = id;
  },

  SET_CAMPAIGN_DATA(state, data) {
    state.campaignData = { ...state.campaignData, ...data };
  },

  SET_FORM_STATE(state, data) {
    state.formState = { ...state.formState, ...data };
  },

  SET_RECIPIENT_LIST_OPTIONS(state, options) {
    state.recipientListOptions = options;
  },

  SET_EXCLUDE_OPTIONS(state, options) {
    state.excludeOptions = options;
  },

  SET_WHATSAPP_CONFIGS(state, configs) {
    state.whatsappConfigs = configs;
  },

  SET_RECIPIENT_COUNT(state, count) {
    state.recipientCount = count;
  },

  SET_LOADING_RECIPIENT_COUNT(state, loading) {
    state.isLoadingRecipientCount = loading;
  },

  SET_INITIALIZING(state, value) {
    state.isInitializing = value;
  },

  SET_SAVING(state, value) {
    state.isSaving = value;
  },

  SET_ORIGINAL_CAMPAIGN_DATA(state, data) {
    state.originalCampaignData = data ? JSON.parse(JSON.stringify(data)) : null;
  },

  RESET_CAMPAIGN_DATA(state) {
    state.campaignData = { ...DEFAULT_WHATSAPP_CAMPAIGN_DATA };
    state.formState = { ...DEFAULT_CAMPAIGN_FORM_STATE };
    state.originalCampaignData = null;
    state.campaignId = null;
    state.campaignMessageId = null;
  },
};

const actions = {
  async fetchRecipientListOptions({ commit }) {
    try {
      const contactLists = await toGetAllEntityLists("contact");
      const leadLists = await toGetAllEntityLists("lead");

      const mapList = (list, entityType) => ({
        ...list,
        id: list.id || list.list_id,
        name: list.list_name || list.name,
        entity_type: list.entity_type || entityType,
      });

      const options = [
        ...contactLists.map((l) => mapList(l, ENTITY_TYPES.CONTACT)),
        ...leadLists.map((l) => mapList(l, ENTITY_TYPES.LEAD)),
      ];

      commit("SET_RECIPIENT_LIST_OPTIONS", options);
      commit("SET_EXCLUDE_OPTIONS", options);
    } catch (error) {
      commit("SET_RECIPIENT_LIST_OPTIONS", []);
      commit("SET_EXCLUDE_OPTIONS", []);
    }
  },

  async fetchWhatsAppConfigs({ commit }) {
    try {
      const response = await IntegrationService.fetchWhatsAppConfigs();
      if (response?.success) {
        commit("SET_WHATSAPP_CONFIGS", response.configs || response.data || []);
      } else {
        commit("SET_WHATSAPP_CONFIGS", []);
      }
    } catch (error) {
      commit("SET_WHATSAPP_CONFIGS", []);
    }
  },

  async fetchRecipientCount({ state, commit }) {
    if (!state.campaignId || !state.campaignData.recipientLists?.length) {
      commit("SET_RECIPIENT_COUNT", 0);
      return;
    }

    try {
      commit("SET_LOADING_RECIPIENT_COUNT", true);

      const separateByType = (ids, options) => {
        const contactIds = [];
        const leadIds = [];
        ids.forEach((id) => {
          const option = options.find((opt) => opt.id === id);
          if (option?.entity_type === ENTITY_TYPES.CONTACT) contactIds.push(id);
          else if (option?.entity_type === ENTITY_TYPES.LEAD) leadIds.push(id);
        });
        return { contactIds, leadIds };
      };

      const included = separateByType(
        state.campaignData.recipientLists,
        state.recipientListOptions
      );
      const excluded = separateByType(
        state.campaignData.excludedLists,
        state.excludeOptions
      );

      const response = await SuperCampaignsService.getCampaignRecipientsCount({
        campaign_id: parseInt(state.campaignId),
        contact_segments_to_include: included.contactIds,
        lead_segments_to_include: included.leadIds,
        contact_segments_to_exclude: excluded.contactIds,
        lead_segments_to_exclude: excluded.leadIds,
      });

      if (response?.member_count !== undefined) {
        commit("SET_RECIPIENT_COUNT", response.member_count || 0);
      }
    } catch (error) {
      commit("SET_RECIPIENT_COUNT", 0);
    } finally {
      commit("SET_LOADING_RECIPIENT_COUNT", false);
    }
  },

  async saveCampaign({ state, commit }, { variableConfigs, templateVariableTypes, messageTemplates }) {
    if (!state.campaignId) return { success: false, error: "No campaign ID" };

    try {
      commit("SET_SAVING", true);

      const separateByType = (ids, options) => {
        const contactIds = [];
        const leadIds = [];
        ids.forEach((id) => {
          const option = options.find((opt) => opt.id === id);
          if (option?.entity_type === ENTITY_TYPES.CONTACT) contactIds.push(id);
          else if (option?.entity_type === ENTITY_TYPES.LEAD) leadIds.push(id);
        });
        return { contactIds, leadIds };
      };

      const included = separateByType(
        state.campaignData.recipientLists,
        state.recipientListOptions
      );
      const excluded = separateByType(
        state.campaignData.excludedLists,
        state.excludeOptions
      );

      const variableConfigurations = {};
      Object.keys(variableConfigs).forEach((key) => {
        const config = variableConfigs[key];
        if (config && (config.mapped_field || config.media_url || config.subtype || config.fallback)) {
          const varConfig = {};
          if (templateVariableTypes[key]?.type) varConfig.type = templateVariableTypes[key].type;
          if (templateVariableTypes[key]?.subtype) varConfig.subtype = templateVariableTypes[key].subtype;
          else if (config.subtype) varConfig.subtype = config.subtype;
          if (config.mapped_field) varConfig.mapped_field = config.mapped_field;
          if (config.media_url) varConfig.media_url = config.media_url;
          if (config.fallback) varConfig.fallback = config.fallback;
          if (Object.keys(varConfig).length > 0) variableConfigurations[key] = varConfig;
        }
      });

      const selectedTemplate = messageTemplates.find(
        (t) => t.id === state.campaignData.messageTemplate
      );

      const payload = {
        campaign_id: parseInt(state.campaignId),
        name: state.campaignData.name,
        contact_segments_to_include: included.contactIds,
        lead_segments_to_include: included.leadIds,
        contact_segments_to_exclude: excluded.contactIds,
        lead_segments_to_exclude: excluded.leadIds,
        message: {
          campaign_message_id: parseInt(state.campaignMessageId),
          whatsapp_config_id: state.campaignData.whatsappConfigId,
          whatsapp_template_id: state.campaignData.messageTemplate,
          whatsapp_message_content: {
            template: selectedTemplate ? { ...selectedTemplate } : null,
            variable_configurations:
              Object.keys(variableConfigurations).length > 0
                ? variableConfigurations
                : null,
          },
        },
      };

      const response = await SuperCampaignsService.saveCampaignWhatsAppDetails(payload);

      if (response?.success === false) {
        return { success: false, error: response.error || response.message };
      }

      commit("SET_ORIGINAL_CAMPAIGN_DATA", state.campaignData);
      return { success: true, message: response?.message };
    } catch (error) {
      return { success: false, error: "An error occurred while saving" };
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async createCampaign({ commit }) {
    try {
      const response = await SuperCampaignsService.createNewCampaign("whatsapp");
      if (response?.campaign_id && response?.campaign_message_id) {
        commit("SET_CAMPAIGN_ID", response.campaign_id);
        commit("SET_CAMPAIGN_MESSAGE_ID", response.campaign_message_id);
        return { success: true, campaignId: response.campaign_id, messageId: response.campaign_message_id };
      }
      return { success: false };
    } catch (error) {
      return { success: false };
    }
  },

  async fetchCampaignDetails({ state, commit, dispatch }) {
    if (!state.campaignId) return;

    try {
      commit("SET_INITIALIZING", true);
      const response = await SuperCampaignsService.getCampaignDetails(state.campaignId);

      if (response?.success && response.campaign) {
        const campaign = response.campaign;

        commit("SET_CAMPAIGN_DATA", {
          name: campaign.name || "",
          recipientLists: [
            ...(campaign.contact_segments_to_include || []),
            ...(campaign.lead_segments_to_include || []),
          ],
          excludedLists: [
            ...(campaign.contact_segments_to_exclude || []),
            ...(campaign.lead_segments_to_exclude || []),
          ],
          excludeSome:
            (campaign.contact_segments_to_exclude?.length || 0) +
              (campaign.lead_segments_to_exclude?.length || 0) >
            0,
        });

        if (campaign.message?.campaign_message_id) {
          commit("SET_CAMPAIGN_MESSAGE_ID", campaign.message.campaign_message_id);
        }

        commit("SET_ORIGINAL_CAMPAIGN_DATA", state.campaignData);
        return { success: true, campaign };
      }
      return { success: false };
    } catch (error) {
      return { success: false };
    } finally {
      commit("SET_INITIALIZING", false);
    }
  },

  updateCampaignData({ commit }, data) {
    commit("SET_CAMPAIGN_DATA", data);
  },

  resetCampaign({ commit }) {
    commit("RESET_CAMPAIGN_DATA");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

